import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  Box,
  Card,
  Chip,
  Stack,
  Button,
  Container,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  DialogTitle,
} from '@mui/material';

import Carousel from 'react-material-ui-carousel';

import { Iconify } from 'src/components/iconify';
import { paths } from 'src/routes/paths';
import { get_faq_by_id } from 'src/auth/context/jwt/faqs';

export default function FaqDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [faq, setFaq] = useState(null);

  const [mediaViewer, setMediaViewer] = useState({
    open: false,
    media: [],
    selectedIndex: 0,
  });

  const fetchDetail = useCallback(async () => {
    try {
      const res = await get_faq_by_id({ id });

      if (res?.status === 200 || res?.status === 201) {
        setFaq(res?.data?.data);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Button
        startIcon={<Iconify icon="eva:arrow-back-fill" />}
        onClick={() => navigate(-1)}
        sx={{ mb: 4 }}
      >
        Back To FAQ
      </Button>

      <Card sx={{ p: 5, borderRadius: 1.5 }}>
        <Stack spacing={3}>
          <Typography
            variant="subtitle1"
            sx={{
              pl: 2,
              borderLeft: '4px solid #8E33FF',
              fontWeight: 700,
            }}
          >
            {faq?.parent_category?.name}

            {faq?.sub_category && ` / ${faq?.sub_category?.name}`}
          </Typography>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 30,
            }}
          >
            {faq?.question}
          </Typography>

          <Typography variant="h6">Answer:</Typography>
          <Typography
            sx={{ fontSize: 13, mt: -4 }}
            dangerouslySetInnerHTML={{
              __html: faq?.answer_text,
            }}
          />

          {!!faq.media?.length && (
            <>
              <Typography variant="h6">Media:</Typography>

              {faq?.media?.length > 0 && (
                <Box
                  sx={{
                    mt: 0,
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2,1fr)',
                      sm: 'repeat(3,1fr)',
                      md: 'repeat(4,1fr)',
                    },
                    gap: 2,
                  }}
                >
                  {faq?.media?.map((item, index) => (
                    <Card
                      key={item?.id}
                      onClick={() =>
                        setMediaViewer({
                          open: true,
                          media: faq?.media,
                          selectedIndex: index,
                        })
                      }
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        height: 180,
                      }}
                    >
                      {item?.media_type === 'image' ? (
                        <Box
                          component="img"
                          src={item?.media}
                          alt={`Media ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Box
                          component="video"
                          muted
                          controls
                          preload="metadata"
                          sx={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                          }}
                        >
                          <source src={item?.media} />
                          <track kind="captions" srcLang="en" label="English captions" />
                        </Box>
                      )}

                      {/* Type Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                          px: 1,
                          py: 0.4,
                          borderRadius: 5,
                          bgcolor: 'rgba(0,0,0,.65)',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {item?.media_type === 'image' ? 'Image' : 'Video'}
                      </Box>
                    </Card>
                  ))}
                </Box>
              )}
            </>
          )}
        </Stack>
      </Card>

      <Dialog
        open={mediaViewer.open}
        onClose={() =>
          setMediaViewer({
            open: false,
            media: [],
            selectedIndex: 0,
          })
        }
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Media
          <IconButton
            sx={{ position: 'absolute', right: 8, top: 8 }}
            onClick={() =>
              setMediaViewer({
                open: false,
                media: [],
                selectedIndex: 0,
              })
            }
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {mediaViewer?.media?.length ? (
            <Carousel autoPlay={false} navButtonsAlwaysVisible indicators>
              {mediaViewer?.media?.map((item) => (
                <Box
                  key={item?.id}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    height: 500,
                  }}
                >
                  {item?.media_type === 'image' ? (
                    <Box
                      component="img"
                      src={item?.media}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: 2,
                      }}
                    />
                  ) : (
                    <video
                      src={item?.media}
                      controls
                      style={{
                        width: '100%',
                        maxHeight: 500,
                        objectFit: 'contain',
                      }}
                    >
                      <track kind="captions" />
                    </video>
                  )}
                </Box>
              ))}
            </Carousel>
          ) : (
            <Typography>No media available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
